import React, { Suspense } from 'react';
import { lazyRetry } from '@/utils/lazyRetry';

import { BrowserRouter, Outlet, Routes, Route, useParams } from 'react-router';
import { PERMITTED_REGIONS } from './regions';

import { GenericRouteHydrator } from '@/utils/routeState/GenericRouteHydrator';
import { DatadogRouteTracker } from '@/utils/DatadogRouteTracker';
import { AnalyticsManager } from '@/utils/analytics';

const CartRouteElement = lazyRetry(() => import('./containers/CartPage/CartRouteElement'));
const Checkout = lazyRetry(() => import('./containers/Checkout/Checkout'));
const LabsCheckout = lazyRetry(() => import('./components/LabsCheckout/LabsCheckout'));
const LabsCheckoutReview = lazyRetry(() => import('./components/LabsCheckout/LabsCheckoutReview'));
const LabsReceipt = lazyRetry(() => import('./components/LabsCheckout/LabsReceipt'));
const SavedPaymentInfo = lazyRetry(() => import('./containers/SavedPaymentInfo'));
const SubscriptionPaymentPage = lazyRetry(() => import('./containers/SubscriptionPaymentPage'));
const SubscriptionReceipt = lazyRetry(() => import('./containers/SubscriptionReceipt'));
const SubscriptionRenewalOptIn = lazyRetry(
  () => import('./containers/SubscriptionRenewalOptIn/SubscriptionRenewalOptIn'),
);
const ReceiptRouteElement = lazyRetry(() => import('./containers/Receipt/ReceiptRouteElement'));
const ReceiptPage = lazyRetry(() => import('./components/Receipt/ReceiptPage'));
const ReceiptPrintPage = lazyRetry(() => import('./components/Receipt/ReceiptPrintPage'));

interface RouteConfig {
  path: string;
  element: React.ComponentType;
  regions?: string[];
  children?: Record<string, RouteConfig>;
}

export const ROUTES: Record<string, RouteConfig> = {
  CART: {
    path: 'cart',
    element: CartRouteElement,
  },
  CHECKOUT: {
    path: 'checkout',
    element: Checkout,
  },
  PLATFORM_CHECKOUT: {
    path: 'platform/checkout',
    element: Checkout,
  },
  BOOK_PAYMENT: {
    path: 'book/payment',
    element: Checkout,
    regions: PERMITTED_REGIONS.US,
  },
  TOTAL_HEALTH_CHECKOUT: {
    path: 'total-health/checkout',
    element: Checkout,
    regions: PERMITTED_REGIONS.US,
  },
  LABS: {
    path: 'labs',
    element: Outlet,
    regions: PERMITTED_REGIONS.US,
    children: {
      CHECKOUT: {
        path: 'checkout',
        element: LabsCheckout,
      },
      REVIEW: {
        path: 'checkout/review',
        element: LabsCheckoutReview,
      },
      RECEIPT: {
        path: 'receipt/:receiptId',
        element: LabsReceipt,
      },
    },
  },
  SAVED_PAYMENTS: {
    path: 'saved-payments',
    element: SavedPaymentInfo,
  },
  SUBSCRIPTION_PAYMENT: {
    path: 'subscription/payment',
    element: SubscriptionPaymentPage,
  },
  UPGRADE_SUBSCRIPTION_PAYMENT: {
    path: 'upgrade-subscription/payment',
    element: SubscriptionPaymentPage,
  },
  RENEW_SUBSCRIPTION_PAYMENT: {
    path: 'renew-subscription/payment',
    element: SubscriptionPaymentPage,
  },
  SUBSCRIPTION_RECEIPT: {
    path: 'subscription/receipt/:subscriptionId',
    element: SubscriptionReceipt,
  },
  RENEWAL_OPT_IN: {
    path: 'renewal-opt-in',
    element: SubscriptionRenewalOptIn,
  },
  RECEIPT: {
    path: 'receipt',
    element: ReceiptRouteElement,
    children: {
      RECEIPT_DETAIL: {
        path: ':receiptId',
        element: ReceiptPage,
      },
      RECEIPT_PRINT: {
        path: ':receiptId/print',
        element: ReceiptPrintPage,
      },
    },
  },
};

type RegionWrapperProps = {
  element: React.ReactElement;
  regions?: string[];
};

// React Router v6+ no longer supports regex in route paths (e.g., /en-:lang(us|ca|gb))
// and doesn't natively validate custom segment patterns like /en-:lang.
// This wrapper acts as a middleware-like guardrail to filter invalid regions before rendering the element.
// Ideally, this should render a proper 404 component. But right now this is handled by Django
const RegionWrapper = ({ element, regions }: RegionWrapperProps) => {
  const { lang } = useParams();
  const region = lang?.split('-')[1]?.toLowerCase();

  if (!regions?.includes(region)) {
    return <span>NOT FOUND</span>;
  }

  return element;
};

const RouteWithContext = ({
  element: Element,
  isSelfHydratingRoute,
  regions,
}: {
  element: React.ComponentType;
  isSelfHydratingRoute: boolean;
  regions: string[];
}) => {
  const wrappedElement = isSelfHydratingRoute ? (
    <Suspense fallback={null}>
      <Element />
    </Suspense>
  ) : (
    <Suspense fallback={null}>
      <GenericRouteHydrator>
        <Element />
      </GenericRouteHydrator>
    </Suspense>
  );

  return <RegionWrapper element={wrappedElement} regions={regions} />;
};

const renderRoute = (
  keyPrefix: string,
  { path, element: Element, regions = PERMITTED_REGIONS.ALL, children }: RouteConfig,
  isChild = false,
): React.ReactElement => {
  const routeKey = `${keyPrefix}${path}`;
  // RouteElements that manage their own hydration bypass GenericRouteHydrator
  const isSelfHydratingRoute = Element === CartRouteElement || Element === ReceiptRouteElement;

  return (
    <Route
      key={routeKey}
      path={isChild ? path : `:lang/${path}`}
      element={
        <RouteWithContext
          element={Element}
          isSelfHydratingRoute={isSelfHydratingRoute}
          regions={regions}
        />
      }
    >
      {children && Object.values(children).map((child) => renderRoute(`${routeKey}/`, child, true))}
    </Route>
  );
};

const AppRoutes = () => {
  // This key is used to gate the reload-once behavior of the lazyRetry utility.
  React.useEffect(() => {
    sessionStorage.removeItem('vite_chunk_reload_once');
  }, []);
  return (
    <BrowserRouter>
      <AnalyticsManager />
      <DatadogRouteTracker />
      <Routes>{Object.values(ROUTES).map((route) => renderRoute('/:lang/', route))}</Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
